import { createSlice } from "@reduxjs/toolkit";
import {
  getAllProducts,
  getAllProductsWithoutPagination,
  getProductById,
  createProduct,
  updateProduct,
  deleteProduct,
  reorderProducts,
  updateProductActive,
} from "actions/products";

const initialState = {
  products: [],
  allProducts: [],
  currentProduct: null,
  loading: false,
  reorderLoading: false,
  isActiveLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
};

const productsSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentProduct: (state) => {
      state.currentProduct = null;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload.data || action.payload;
        if (action.payload.meta) {
          state.pagination = {
            page: action.payload.meta.page,
            limit: action.payload.meta.limit,
            total: action.payload.meta.total,
            totalPages: action.payload.meta.totalPages,
          };
        }
      })
      .addCase(getAllProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    builder
      .addCase(getAllProductsWithoutPagination.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllProductsWithoutPagination.fulfilled, (state, action) => {
        state.loading = false;
        state.allProducts = action.payload;
      })
      .addCase(getAllProductsWithoutPagination.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    builder
      .addCase(getProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProductById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProduct = action.payload;
      })
      .addCase(getProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    builder
      .addCase(createProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.products.unshift(action.payload);
        state.allProducts.unshift(action.payload);
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    builder
      .addCase(updateProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProduct.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.products.findIndex(
          (product) => product.id === action.payload.id,
        );
        if (index !== -1) {
          state.products[index] = action.payload;
        }
        const allIndex = state.allProducts.findIndex(
          (product) => product.id === action.payload.id,
        );
        if (allIndex !== -1) {
          state.allProducts[allIndex] = action.payload;
        }
        if (state.currentProduct?.id === action.payload.id) {
          state.currentProduct = action.payload;
        }
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    builder
      .addCase(deleteProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.loading = false;
        state.products = state.products.filter(
          (product) => product.id !== action.payload,
        );
        state.allProducts = state.allProducts.filter(
          (product) => product.id !== action.payload,
        );
        if (state.currentProduct?.id === action.payload) {
          state.currentProduct = null;
        }
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });

    builder
      .addCase(reorderProducts.pending, (state) => {
        state.reorderLoading = true;
        state.error = null;
      })
      .addCase(reorderProducts.fulfilled, (state, action) => {
        state.reorderLoading = false;
        state.products = action.payload;
        const orderMap = new Map(action.payload.map((p) => [p.id, p.order]));
        state.allProducts = state.allProducts.map((p) =>
          orderMap.has(p.id) ? { ...p, order: orderMap.get(p.id) } : p,
        );
      })
      .addCase(reorderProducts.rejected, (state, action) => {
        state.reorderLoading = false;
        state.error = action.error.message;
      });

    builder
      .addCase(updateProductActive.pending, (state) => {
        state.isActiveLoading = true;
        state.error = null;
      })
      .addCase(updateProductActive.fulfilled, (state, action) => {
        state.isActiveLoading = false;
        const index = state.products.findIndex(
          (product) => product.id === action.payload.id,
        );
        if (index !== -1) {
          state.products[index] = action.payload;
        }
        const allIndex = state.allProducts.findIndex(
          (product) => product.id === action.payload.id,
        );
        if (allIndex !== -1) {
          state.allProducts[allIndex] = action.payload;
        }
      })
      .addCase(updateProductActive.rejected, (state, action) => {
        state.isActiveLoading = false;
        state.error = action.error.message;
      });
  },
});

export const { clearError, clearCurrentProduct, setPagination } =
  productsSlice.actions;
export default productsSlice.reducer;
